import React, { useState, useEffect } from 'react';
import { proyectos, clientesRentabilidad } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Briefcase, Plus, Edit2, Trash2, Eye, DollarSign, TrendingUp,
  Calendar, Users, Clock, CheckCircle, AlertCircle, XCircle
} from 'lucide-react';

const ESTADOS = [
  { value: 'planificacion', label: 'Planificación', color: 'bg-slate-100 text-slate-800', icon: Clock },
  { value: 'en_progreso', label: 'En Progreso', color: 'bg-blue-100 text-blue-800', icon: TrendingUp },
  { value: 'completado', label: 'Completado', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
];

const Proyectos = () => {
  const [data, setData] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCostoDialog, setShowCostoDialog] = useState(false);
  const [showIngresoDialog, setShowIngresoDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  
  // Form
  const [formData, setFormData] = useState({
    cliente_rentabilidad_id: '',
    nombre_proyecto: '',
    fecha_inicio: '',
    fecha_fin: '',
    estado: 'planificacion',
    descripcion: '',
    presupuesto: ''
  });

  const [costoForm, setCostoForm] = useState({ tipo: '', descripcion: '', monto: '' });
  const [ingresoForm, setIngresoForm] = useState({ descripcion: '', monto: '', fecha: '', pagado: false });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filtroEstado !== 'todos' ? { estado: filtroEstado } : {};
      const [proyectosRes, clientesRes] = await Promise.all([
        proyectos.getAll(params),
        clientesRentabilidad.getAll()
      ]);
      setData(proyectosRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      toast.error('Error al cargar proyectos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filtroEstado]);

  const fetchProjectDetail = async (projectId) => {
    try {
      const response = await proyectos.getDetalle(projectId);
      setProjectDetail(response.data);
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  const handleSubmit = async () => {
    if (!formData.cliente_rentabilidad_id || !formData.nombre_proyecto) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      if (editingItem) {
        await proyectos.update(editingItem.id, formData);
        toast.success('Proyecto actualizado');
      } else {
        await proyectos.create(formData);
        toast.success('Proyecto creado');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este proyecto y todos sus costos/ingresos?')) return;
    try {
      await proyectos.delete(id);
      toast.success('Proyecto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleAddCosto = async () => {
    if (!costoForm.tipo || !costoForm.monto) {
      toast.error('Completa los campos requeridos');
      return;
    }
    try {
      await proyectos.addCosto(selectedProject.id, costoForm);
      toast.success('Costo agregado');
      setShowCostoDialog(false);
      setCostoForm({ tipo: '', descripcion: '', monto: '' });
      fetchProjectDetail(selectedProject.id);
    } catch (error) {
      toast.error('Error al agregar costo');
    }
  };

  const handleAddIngreso = async () => {
    if (!ingresoForm.descripcion || !ingresoForm.monto) {
      toast.error('Completa los campos requeridos');
      return;
    }
    try {
      await proyectos.addIngreso(selectedProject.id, ingresoForm);
      toast.success('Ingreso agregado');
      setShowIngresoDialog(false);
      setIngresoForm({ descripcion: '', monto: '', fecha: '', pagado: false });
      fetchProjectDetail(selectedProject.id);
    } catch (error) {
      toast.error('Error al agregar ingreso');
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_rentabilidad_id: '',
      nombre_proyecto: '',
      fecha_inicio: '',
      fecha_fin: '',
      estado: 'planificacion',
      descripcion: '',
      presupuesto: ''
    });
    setEditingItem(null);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      cliente_rentabilidad_id: item.cliente_rentabilidad_id,
      nombre_proyecto: item.nombre_proyecto,
      fecha_inicio: item.fecha_inicio || '',
      fecha_fin: item.fecha_fin || '',
      estado: item.estado,
      descripcion: item.descripcion || '',
      presupuesto: item.presupuesto || ''
    });
    setShowDialog(true);
  };

  const openDetail = async (item) => {
    setSelectedProject(item);
    await fetchProjectDetail(item.id);
    setShowDetailDialog(true);
  };

  const getEstadoInfo = (estado) => ESTADOS.find(e => e.value === estado) || ESTADOS[0];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
  };

  // Summary stats
  const stats = {
    total: data.length,
    enProgreso: data.filter(p => p.estado === 'en_progreso').length,
    completados: data.filter(p => p.estado === 'completado').length,
    ingresos: data.reduce((sum, p) => sum + (p.ingresos || 0), 0),
    utilidad: data.reduce((sum, p) => sum + (p.utilidad || 0), 0)
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="proyectos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-primary" />
            Gestión de Proyectos
          </h1>
          <p className="text-muted-foreground text-sm">Control de rentabilidad por proyecto</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="new-project-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proyecto
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Proyectos</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Briefcase className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">En Progreso</p>
                <p className="text-2xl font-bold">{stats.enProgreso}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ingresos</p>
                <p className="text-xl font-bold text-emerald-600">{formatCurrency(stats.ingresos)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Utilidad Total</p>
                <p className={`text-xl font-bold ${stats.utilidad >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(stats.utilidad)}
                </p>
              </div>
              <TrendingUp className={`w-8 h-8 ${stats.utilidad >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-2">
            <Button 
              variant={filtroEstado === 'todos' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFiltroEstado('todos')}
            >
              Todos ({data.length})
            </Button>
            {ESTADOS.map(e => (
              <Button 
                key={e.value}
                variant={filtroEstado === e.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFiltroEstado(e.value)}
                className="gap-1"
              >
                <e.icon className="w-3 h-3" />
                {e.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Projects List */}
      <Card>
        <CardHeader>
          <CardTitle>Proyectos</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando...</div>
          ) : data.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay proyectos</p>
              <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowDialog(true); }}>
                Crear primer proyecto
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {data.map((item) => {
                const estadoInfo = getEstadoInfo(item.estado);
                const porcentajePresupuesto = item.presupuesto > 0 
                  ? (item.costos / item.presupuesto) * 100 
                  : 0;
                
                return (
                  <div key={item.id} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors" data-testid={`project-item-${item.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{item.nombre_proyecto}</h3>
                          <Badge className={estadoInfo.color}>
                            <estadoInfo.icon className="w-3 h-3 mr-1" />
                            {estadoInfo.label}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-3 flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {item.cliente_nombre || 'Sin cliente'}
                        </p>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Ingresos</p>
                            <p className="font-medium text-emerald-600">{formatCurrency(item.ingresos)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Costos</p>
                            <p className="font-medium text-red-600">{formatCurrency(item.costos)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Utilidad</p>
                            <p className={`font-medium ${item.utilidad >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(item.utilidad)}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Margen</p>
                            <p className={`font-medium ${item.margen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {item.margen}%
                            </p>
                          </div>
                        </div>
                        
                        {item.presupuesto > 0 && (
                          <div className="mt-3">
                            <div className="flex justify-between text-xs text-muted-foreground mb-1">
                              <span>Presupuesto: {formatCurrency(item.presupuesto)}</span>
                              <span>{porcentajePresupuesto.toFixed(1)}% usado</span>
                            </div>
                            <Progress 
                              value={Math.min(porcentajePresupuesto, 100)} 
                              className={`h-2 ${porcentajePresupuesto > 100 ? '[&>div]:bg-red-500' : ''}`}
                            />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 self-end lg:self-center">
                        <Button variant="outline" size="sm" onClick={() => openDetail(item)} data-testid={`view-project-${item.id}`}>
                          <Eye className="w-4 h-4 mr-1" />
                          Detalle
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Proyecto' : 'Nuevo Proyecto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Cliente *</Label>
              <Select value={formData.cliente_rentabilidad_id} onValueChange={(v) => setFormData({ ...formData, cliente_rentabilidad_id: v })}>
                <SelectTrigger data-testid="form-client">
                  <SelectValue placeholder="Selecciona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre del Proyecto *</Label>
              <Input
                value={formData.nombre_proyecto}
                onChange={(e) => setFormData({ ...formData, nombre_proyecto: e.target.value })}
                placeholder="Ej: Campaña Q1 2025"
                data-testid="form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha Inicio</Label>
                <Input
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha Fin</Label>
                <Input
                  type="date"
                  value={formData.fecha_fin}
                  onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={formData.estado} onValueChange={(v) => setFormData({ ...formData, estado: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ESTADOS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Presupuesto</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.presupuesto}
                onChange={(e) => setFormData({ ...formData, presupuesto: parseFloat(e.target.value) || '' })}
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Notas adicionales..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} data-testid="submit-project">
              {editingItem ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              {projectDetail?.nombre_proyecto}
            </DialogTitle>
          </DialogHeader>
          
          {projectDetail && (
            <Tabs defaultValue="resumen" className="mt-4">
              <TabsList className="w-full">
                <TabsTrigger value="resumen" className="flex-1">Resumen</TabsTrigger>
                <TabsTrigger value="costos" className="flex-1">Costos ({projectDetail.costos?.length || 0})</TabsTrigger>
                <TabsTrigger value="ingresos" className="flex-1">Ingresos ({projectDetail.ingresos?.length || 0})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="resumen" className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-muted-foreground">Ingresos</p>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(projectDetail.total_ingresos)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-muted-foreground">Costos</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(projectDetail.total_costos)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-muted-foreground">Utilidad</p>
                      <p className={`text-xl font-bold ${projectDetail.utilidad >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(projectDetail.utilidad)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-sm text-muted-foreground">Margen</p>
                      <p className={`text-xl font-bold ${projectDetail.margen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {projectDetail.margen}%
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Cliente: <span className="font-medium text-foreground">{projectDetail.cliente_nombre}</span></p>
                  {projectDetail.descripcion && <p className="text-sm mt-2">{projectDetail.descripcion}</p>}
                </div>
              </TabsContent>
              
              <TabsContent value="costos">
                <div className="flex justify-end mb-4">
                  <Button size="sm" onClick={() => { setShowCostoDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar Costo
                  </Button>
                </div>
                {projectDetail.costos?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No hay costos registrados</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {projectDetail.costos?.map((c, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 border rounded">
                        <div>
                          <p className="font-medium">{c.tipo}</p>
                          <p className="text-sm text-muted-foreground">{c.descripcion}</p>
                        </div>
                        <p className="font-semibold text-red-600">{formatCurrency(c.monto)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="ingresos">
                <div className="flex justify-end mb-4">
                  <Button size="sm" onClick={() => { setShowIngresoDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar Ingreso
                  </Button>
                </div>
                {projectDetail.ingresos?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">No hay ingresos registrados</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {projectDetail.ingresos?.map((i, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 border rounded">
                        <div>
                          <p className="font-medium">{i.descripcion}</p>
                          <p className="text-sm text-muted-foreground">
                            {i.fecha} {i.pagado ? <Badge variant="outline" className="ml-2">Pagado</Badge> : <Badge variant="outline" className="bg-amber-50">Pendiente</Badge>}
                          </p>
                        </div>
                        <p className="font-semibold text-emerald-600">{formatCurrency(i.monto)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Costo Dialog */}
      <Dialog open={showCostoDialog} onOpenChange={setShowCostoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Costo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo *</Label>
              <Select value={costoForm.tipo} onValueChange={(v) => setCostoForm({ ...costoForm, tipo: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="colaborador">Colaborador</SelectItem>
                  <SelectItem value="herramienta">Herramienta</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="servicio">Servicio Externo</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={costoForm.descripcion}
                onChange={(e) => setCostoForm({ ...costoForm, descripcion: e.target.value })}
                placeholder="Detalle del costo"
              />
            </div>
            <div>
              <Label>Monto *</Label>
              <Input
                type="number"
                value={costoForm.monto}
                onChange={(e) => setCostoForm({ ...costoForm, monto: parseFloat(e.target.value) || '' })}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCostoDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddCosto}>Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Ingreso Dialog */}
      <Dialog open={showIngresoDialog} onOpenChange={setShowIngresoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Ingreso</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Descripción *</Label>
              <Input
                value={ingresoForm.descripcion}
                onChange={(e) => setIngresoForm({ ...ingresoForm, descripcion: e.target.value })}
                placeholder="Concepto del ingreso"
              />
            </div>
            <div>
              <Label>Monto *</Label>
              <Input
                type="number"
                value={ingresoForm.monto}
                onChange={(e) => setIngresoForm({ ...ingresoForm, monto: parseFloat(e.target.value) || '' })}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                value={ingresoForm.fecha}
                onChange={(e) => setIngresoForm({ ...ingresoForm, fecha: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="pagado"
                checked={ingresoForm.pagado}
                onChange={(e) => setIngresoForm({ ...ingresoForm, pagado: e.target.checked })}
              />
              <Label htmlFor="pagado">Pagado</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIngresoDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddIngreso}>Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Proyectos;
